import { TableLocal, TableLocalPagination } from "components/Common/TableLocal"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { Badge } from "reactstrap"
import ButtonTooltip from "components/Common/ButtonTooltip"
import ConfirmModal from "components/Common/ConfirmModal"
import DeleteModal from "components/Common/DeleteModal"
import DuplicateCheckModal from "components/Common/DuplicateCheckModal"
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"
import { downloadAndOpenFile } from "api/pdfApi"
import {
  getQuotationExistingReceipts,
  getQuotationSimilar,
} from "api/quotationApi"
import { useCreateMoneyReceiptFromQuotation } from "hooks/useMoneyReceipt"
import { useDeleteQuotation, useDuplicateQuotation } from "hooks/useQuotation"
import { useToastMessage } from "hooks/useToastMessage"
import { useListParams } from "hooks/useListParams"

const getVehicleInfo = (specs = [], fieldName) =>
  specs?.find(item => item?.key === fieldName)?.value ?? "-"

const QuotationList = ({ data, onChanged }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { handleResponse, handleError } = useToastMessage()
  const { params, updateParams } = useListParams({
    q_page: 1,
    q_per_page: 5,
  })
  const currentPage = Number(params?.q_page) || 1
  const itemsPerPage = Number(params?.q_per_page) || 5
  const focusId = params?.q_focus ?? null
  const [downloadingId, setDownloadingId] = useState(null)
  const [checkingDuplicate, setCheckingDuplicate] = useState(false)
  const [duplicateCheckModal, setDuplicateCheckModal] = useState(null)
  const [deleteModal, setDeleteModal] = useState(null)
  const [confirmModal, setConfirmModal] = useState(null)
  const focusHandledRef = useRef(false)
  const focusRowRef = useRef(null)

  const { mutateAsync: deleteQuotation, isPending: isDeleting } =
    useDeleteQuotation()
  const { mutateAsync: duplicateQuotation, isPending: isDuplicating } =
    useDuplicateQuotation()
  const { mutateAsync: createFromQuotation, isPending: creatingReceipt } =
    useCreateMoneyReceiptFromQuotation()

  const sortedContents = useMemo(() => {
    if (!data) return []
    const contents = Array.isArray(data) ? data : []
    return [...contents].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    )
  }, [data])

  const handlePageChange = useCallback(
    page => updateParams({ q_page: page }),
    [updateParams]
  )
  const handleItemsPerPageChange = useCallback(
    n => updateParams({ q_per_page: n, q_page: 1 }),
    [updateParams]
  )

  useEffect(() => {
    if (!focusId || focusHandledRef.current || !sortedContents.length) return
    const idx = sortedContents.findIndex(
      it =>
        String(it.id) === String(focusId) ||
        String(it.qid) === String(focusId)
    )
    if (idx === -1) return
    const targetPage = Math.floor(idx / itemsPerPage) + 1
    if (targetPage !== currentPage) {
      updateParams({ q_page: targetPage })
    }
    focusHandledRef.current = true
  }, [focusId, sortedContents, itemsPerPage, currentPage, updateParams])

  useEffect(() => {
    if (!focusId) return
    const node = focusRowRef.current
    if (!node) return
    node.scrollIntoView({ behavior: "smooth", block: "center" })
    node.classList.add("table-warning")
    const timer = setTimeout(() => {
      node.classList.remove("table-warning")
      updateParams({ q_focus: undefined })
    }, 2500)
    return () => clearTimeout(timer)
  }, [focusId, currentPage, sortedContents, updateParams])

  const handleDownload = async (rowId, identifier) => {
    setDownloadingId(rowId)
    try {
      await downloadAndOpenFile({ url: `/quotations/${identifier}/pdf` })
    } catch (error) {
      handleError(error, "Failed to download quotation PDF.")
    } finally {
      setDownloadingId(null)
    }
  }

  const handleDelete = async item => {
    try {
      const response = await deleteQuotation({ id: item.id })
      handleResponse(response, "Quotation deleted successfully.", true)
      setDeleteModal(null)
      onChanged?.()
    } catch (error) {
      handleError(error, "Failed to delete quotation.")
    }
  }

  const doDuplicate = async item => {
    try {
      const response = await duplicateQuotation(item.id)
      handleResponse(response, "Quotation duplicated successfully.", true)
      const newId = response?.data?.qid ?? response?.data?.id
      if (newId) {
        navigate(`/quotation-list/${newId}/edit`, { state: { from: location } })
      } else {
        onChanged?.()
      }
    } catch (error) {
      handleError(error, "Failed to duplicate quotation.")
    }
  }

  const handleDuplicate = async item => {
    setCheckingDuplicate(true)
    try {
      const res = await getQuotationSimilar(item.id)
      const existing = res?.data?.items ?? []
      if (existing.length > 0) {
        setDuplicateCheckModal({
          type: "quotation",
          item,
          existingItems: existing.map(q => ({
            id: q.id,
            label: q.qid ?? `Q #${q.id}`,
            subLabel: `Status: ${q.status?.name ?? "Pending"} · ${formatReadableDate(q.created_at, "MMM d, y")}`,
            linkTo: `/quotation-list/${q.qid ?? q.id}/view`,
          })),
        })
        return
      }
    } catch {
      // on check failure, proceed silently
    } finally {
      setCheckingDuplicate(false)
    }
    await doDuplicate(item)
  }

  const doCreateReceipt = async item => {
    try {
      const response = await createFromQuotation({
        quotation_id: item.id,
        issued_date: new Date().toISOString().split("T")[0],
      })
      const moneyReceiptId = response?.data?.id
      if (moneyReceiptId) {
        navigate(`/money-receipts/${moneyReceiptId}`)
      }
    } catch (error) {
      handleError(error, "Failed to create money receipt from quotation.")
    }
  }

  const handleCreateReceipt = async item => {
    setCheckingDuplicate(true)
    try {
      const res = await getQuotationExistingReceipts(item.id)
      const existing = res?.data?.items ?? []
      if (existing.length > 0) {
        setDuplicateCheckModal({
          type: "receipt",
          item,
          existingItems: existing.map(mr => ({
            id: mr.id,
            label: mr.mr_no ?? `MR #${mr.id}`,
            subLabel: `Issued: ${formatReadableDate(mr.issued_date, "MMM d, y")}`,
            linkTo: `/money-receipts/${mr.id}`,
          })),
        })
        return
      }
    } catch {
      // on check failure, proceed silently
    } finally {
      setCheckingDuplicate(false)
    }
    await doCreateReceipt(item)
  }

  const handleConfirmAction = async () => {
    const { type, item } = confirmModal
    setConfirmModal(null)
    if (type === "duplicate") {
      await handleDuplicate(item)
    } else {
      await handleCreateReceipt(item)
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "Quotation",
        cell: item => {
          const specs = item?.pdf_content?.vehicle_specs ?? []
          return (
            <div>
              <div className="fw-bold text-primary mb-1">
                <Link to={`/quotation-list/${item?.qid ?? item?.id}/view`}>
                  {item?.qid ?? "-"}
                </Link>
              </div>
              <div className="mb-1">{getVehicleInfo(specs, "Vehicle Name")}</div>
              <div className="small text-muted">
                Stock: {item?.vehicle?.stock_id ?? "-"}
              </div>
              <div className="small text-muted">
                Chassis: {getVehicleInfo(specs, "Chassis No")}
              </div>
            </div>
          )
        },
      },
      {
        header: "Customer",
        cell: item => (
          <div>
            <div className="fw-semibold">{item?.customer?.name ?? "-"}</div>
            <div className="small text-muted">
              {item?.customer?.mobile ?? "-"}
            </div>
            <div className="small text-muted">
              {item?.customer?.email ?? "-"}
            </div>
          </div>
        ),
      },
      {
        header: "Amount",
        cell: item => {
          const priceItems = item?.pdf_content?.price_items ?? []
          return (
            <div>
              {priceItems.map((priceItem, index) => (
                <div
                  key={priceItem?.id ?? `price-${index}`}
                  className="small text-muted d-flex justify-content-between gap-2"
                >
                  <span className="text-truncate">
                    {priceItem?.description || "-"}
                  </span>
                  <span>{formatBDT(priceItem?.amount)}</span>
                </div>
              ))}
              <div className="fw-bold text-success border-top pt-1 mt-1 d-flex justify-content-between gap-2">
                <span>Total</span>
                <span>{formatBDT(item?.pdf_content?.total_amount)}</span>
              </div>
              <div className="small text-muted mt-1">
                Valid:{" "}
                {formatReadableDate(
                  item?.pdf_content?.valid_until,
                  "MMM d, y"
                )}
              </div>
            </div>
          )
        },
      },
      {
        header: "Status",
        cell: item => {
          const status = item?.status
          const color =
            status?.slug === "approved"
              ? "success"
              : status?.slug === "rejected"
              ? "danger"
              : "warning"
          return <Badge color={color}>{status?.name ?? "Pending"}</Badge>
        },
      },
      {
        header: "Date",
        cell: item => (
          <div>
            <div>
              {formatReadableDate(
                item?.pdf_content?.quotation_date,
                "MMM d, y"
              )}
            </div>
            <div className="small text-muted">
              Created: {formatReadableDate(item?.created_at, "MMM d, y")}
            </div>
          </div>
        ),
      },
      {
        header: "Action",
        cell: item => (
          <ul className="list-unstyled d-flex flex-wrap gap-1 mb-0">
            <li>
              <ButtonTooltip
                type="view"
                btnId={`quotation-view-${item.id}`}
                onClick={() =>
                  navigate(`/quotation-list/${item?.qid ?? item?.id}/view`, {
                    state: { from: location },
                  })
                }
              />
            </li>
            <li>
              <ButtonTooltip
                type="edit"
                btnId={`quotation-edit-${item.id}`}
                onClick={() =>
                  navigate(`/quotation-list/${item?.qid ?? item?.id}/edit`, {
                    state: { from: location },
                  })
                }
              />
            </li>
            <li>
              <ButtonTooltip
                type="duplicate"
                btnId={`quotation-duplicate-${item.id}`}
                loading={isDuplicating || checkingDuplicate}
                onClick={() => setConfirmModal({ type: "duplicate", item })}
              />
            </li>
            <li>
              <ButtonTooltip
                type="pdf"
                btnId={`quotation-pdf-${item.id}`}
                loading={downloadingId === item.id}
                onClick={() => handleDownload(item.id, item?.qid ?? item?.id)}
              />
            </li>
            <li>
              <ButtonTooltip
                type="receipt"
                btnId={`quotation-mr-${item.id}`}
                loading={creatingReceipt || checkingDuplicate}
                onClick={() => setConfirmModal({ type: "receipt", item })}
              />
            </li>
            <li>
              <ButtonTooltip
                type="delete"
                btnId={`quotation-delete-${item.id}`}
                loading={isDeleting}
                onClick={() => setDeleteModal(item)}
              />
            </li>
          </ul>
        ),
      },
    ],
    [navigate, location, creatingReceipt, isDeleting, isDuplicating, downloadingId, checkingDuplicate]
  )

  return (
    <>
      <TableLocal
        columns={columns}
        data={sortedContents}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        tableClass="align-middle table-hover mb-0"
        getRowKey={row => row.id}
        rowProps={row => {
          const isFocus =
            focusId &&
            (String(row.id) === String(focusId) ||
              String(row.qid) === String(focusId))
          if (!isFocus) return {}
          return { ref: focusRowRef }
        }}
        emptyMessage={
          <div className="text-center py-5">
            <i className="bx bx-file bx-3x text-muted mb-3"></i>
            <h5 className="text-muted">No quotations found</h5>
            <p className="text-muted mb-0">
              Create your first quotation to get started
            </p>
          </div>
        }
      />

      <TableLocalPagination
        data={sortedContents}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        showItemsPerPage={true}
        itemsPerPageOptions={[5, 10, 20]}
        className="px-3 py-3 border-top"
      />

      <DeleteModal
        show={!!deleteModal}
        onDeleteClick={() => handleDelete(deleteModal)}
        onCloseClick={() => setDeleteModal(null)}
        loading={isDeleting}
      />

      <ConfirmModal
        isOpen={!!confirmModal}
        toggle={() => setConfirmModal(null)}
        title={confirmModal?.type === "duplicate" ? "Duplicate Quotation?" : "Create Money Receipt?"}
        message={
          confirmModal?.type === "duplicate"
            ? "This will create a copy of the quotation. Do you want to proceed?"
            : "This will create a new money receipt from this quotation. Do you want to proceed?"
        }
        confirmLabel={confirmModal?.type === "duplicate" ? "Duplicate" : "Create"}
        icon={confirmModal?.type === "duplicate" ? "mdi-content-copy" : "mdi-receipt"}
        onConfirm={handleConfirmAction}
        isConfirming={isDuplicating || creatingReceipt || checkingDuplicate}
      />

      {duplicateCheckModal && (
        <DuplicateCheckModal
          isOpen={!!duplicateCheckModal}
          toggle={() => setDuplicateCheckModal(null)}
          title={
            duplicateCheckModal.type === "receipt"
              ? "Existing Money Receipts Found"
              : "Similar Quotations Found"
          }
          warningText={
            duplicateCheckModal.type === "receipt"
              ? `${duplicateCheckModal.existingItems.length} money receipt(s) already exist for this customer & vehicle.`
              : `${duplicateCheckModal.existingItems.length} similar quotation(s) found for this customer & vehicle.`
          }
          items={duplicateCheckModal.existingItems}
          proceedLabel={
            duplicateCheckModal.type === "receipt"
              ? "Create New Anyway"
              : "Duplicate Anyway"
          }
          onProceed={async () => {
            const { type, item } = duplicateCheckModal
            setDuplicateCheckModal(null)
            if (type === "receipt") {
              await doCreateReceipt(item)
            } else {
              await doDuplicate(item)
            }
          }}
          isProceeding={creatingReceipt || isDuplicating}
        />
      )}
    </>
  )
}

export default QuotationList
